
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@/hooks/useUser';
import { useViewAs } from '@/contexts/ViewAsContext';
import { useIsSystemAdmin } from '@/hooks/useIsSystemAdmin';
import { 
  mainMenuItems, 
  candidateHQItems, 
  clientHQItems, 
  aiHQItems, 
  opsHQItems, 
  NavItemType 
} from './navItems';
import { SidebarHeader } from './SidebarHeader';
import { SidebarNavGroup } from './SidebarNavGroup';

interface SidebarCoreContentProps {
  collapsed: boolean;
  onLinkClick?: () => void;
}

export const SidebarCoreContent: React.FC<SidebarCoreContentProps> = ({ collapsed, onLinkClick }) => {
  const location = useLocation();
  const { user } = useUser();
  const { viewingAs } = useViewAs();
  const { data: isSystemAdmin, isLoading: isSystemAdminLoading } = useIsSystemAdmin();

  let determinedCurrentRole: string | undefined | null = null;
  if (viewingAs && viewingAs.role) {
    determinedCurrentRole = viewingAs.role;
  } else if (user?.app_metadata?.userrole) {
    determinedCurrentRole = user.app_metadata.userrole;
  }

  const getFilteredNavItems = (items: NavItemType[]): NavItemType[] => {
    let filteredItems = items;

    // Filter for viewer role
    if (determinedCurrentRole === "viewer") {
      const allowedHrefs = ["/dashboard", "/calendar", "/messages", "/candidates", "/jobs", "/clients"];
      filteredItems = items.filter(item => {
        if (item.type === 'separator') return true;
        return allowedHrefs.includes(item.href);
      });
    }

    // Filter for system admin only items - only show if we're not loading and user is system admin
    if (!isSystemAdminLoading) {
      filteredItems = filteredItems.filter(item => {
        if (item.type === 'link' && item.systemAdminOnly) {
          return isSystemAdmin === true;
        }
        return true;
      });
    } else {
      // While loading, hide system admin only items to prevent flashing
      filteredItems = filteredItems.filter(item => {
        if (item.type === 'link' && item.systemAdminOnly) {
          return false;
        }
        return true;
      });
    }

    return filteredItems;
  };

  const filteredMainMenuItems = getFilteredNavItems(mainMenuItems);
  const filteredCandidateHQItems = getFilteredNavItems(candidateHQItems);
  const filteredClientHQItems = getFilteredNavItems(clientHQItems);
  const filteredAiHQItems = getFilteredNavItems(aiHQItems);
  const filteredOpsHQItems = getFilteredNavItems(opsHQItems);

  return (
    <>
      <SidebarHeader collapsed={collapsed} />
      <div className="flex-1 flex flex-col overflow-y-auto space-y-4 no-scrollbar p-0">
        <SidebarNavGroup
          title="Main Menu"
          items={filteredMainMenuItems}
          collapsed={collapsed}
          currentPathname={location.pathname}
          onLinkClick={onLinkClick}
        />
        
        <SidebarNavGroup
          title="Candidate HQ"
          items={filteredCandidateHQItems}
          collapsed={collapsed}
          currentPathname={location.pathname}
          onLinkClick={onLinkClick}
        />
        
        <SidebarNavGroup
          title="Client HQ"
          items={filteredClientHQItems}
          collapsed={collapsed}
          currentPathname={location.pathname}
          onLinkClick={onLinkClick}
        />
        
        <SidebarNavGroup
          title="AI HQ"
          items={filteredAiHQItems}
          collapsed={collapsed}
          currentPathname={location.pathname}
          onLinkClick={onLinkClick}
        />
        
        <div className="mt-auto">
          <SidebarNavGroup
            title="Ops HQ"
            items={filteredOpsHQItems}
            collapsed={collapsed}
            currentPathname={location.pathname}
            onLinkClick={onLinkClick}
          />
        </div>
      </div>
    </>
  );
};
